/* Copyright (c) 2020-present CloudIO Inc */

import {
  areEqualShallow,
  Path,
  useStateXAddToUndo,
  useStateXAtom,
  useStateXGetter,
  useStateXSetter,
  useStateXValue,
} from '@cloudio-saas/statex';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Theme,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  AnyManager,
  getManager,
  ItemID,
  Platform,
  PropertyDefinition,
} from 'componentregistry';
import {
  activeItemIdsAtom,
  designPath,
  itemAtom,
  runtimePath,
} from 'designer/atoms';
import Icon from 'icons/Icon';
import DeferredEditor from 'layout/Sidebar/panels/PropertiesView/property-editors/DeferredEditor';
import React, { useCallback } from 'react';
import { itemWithDefaultValues } from 'registry/Components';
import { getDefaultValues } from './property-editors/PropertyEditorUtils';
import { propertySectionExpandedAtom } from '../../atoms';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    boxShadow: 'none',
    margin: 0,
    '&.Mui-expanded': {
      margin: 0,
    },
  },
  header: {
    alignItems: 'center',
    minHeight: 40,
    '& > div': {
      alignItems: 'center',
      margin: 0,
    },
    '& > .Mui-expanded': {
      margin: 0,
      minHeight: 40,
    },
    '&.Mui-expanded': {
      margin: 0,
      minHeight: 40,
    },
  },
  details: {
    paddingTop: 0,
    paddingBottom: 0,
    '&:last-child': {
      marginBottom: 20,
    },
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: theme.typography.fontWeightRegular,
    lineHeight: '40px',
  },
  form: {
    display: 'block',
    width: '100%',
    '& > div': {
      marginTop: '8px',
    },
  },
}));

interface Props {
  activeItemId: ItemID;
  appUid: string;
  hmr: number;
  mgr: AnyManager;
  pageId: ItemID;
  preventCollapse?: boolean;
  properties: Record<string, PropertyDefinition>;
  section: string;
  platform: Platform;
}

export default function PropertiesSection({
  activeItemId,
  appUid,
  hmr,
  mgr,
  pageId,
  preventCollapse = false,
  properties,
  section,
  platform,
}: Props): React.ReactElement {
  const classes = useStyles();
  const get = useStateXGetter();
  const [expanded, setExpanded] = useStateXAtom(propertySectionExpandedAtom, {
    params: { section },
  });
  const addToUndo = useStateXAddToUndo('io');
  const path = designPath({
    appUid,
    pageId,
    itemId: activeItemId,
    key: 'props',
  });
  const set = useStateXSetter();
  const props = useStateXValue<Record<string, unknown>>(path, mgr.defaultValue);
  const onChange = useCallback(
    (property: string, value: unknown, undoable = true) => {
      if (undoable) {
        addToUndo(designPath({ appUid, pageId }), pageId);
      }
      let activeItemIds = get(activeItemIdsAtom, {
        params: { appUid, pageId },
      });
      if (!activeItemIds.length) {
        activeItemIds = [activeItemId];
      }
      const runtimepaths: Path[] = [];
      activeItemIds.forEach((id) => {
        // multi select property change (bulk edit)
        set<Record<string, unknown>>(
          designPath({ appUid, pageId, itemId: id, key: 'props' }),
          (p) => {
            let item = get(itemAtom, {
              params: { appUid, pageId, itemId: id },
            });
            if (!item) return p;
            const manager = getManager(item.type);
            item = itemWithDefaultValues(item);
            const _props = {
              ...p,
              // @ts-ignore dynamic property & value
              ...manager.onPropertyChange(property, value, item.props, {
                appUid,
                pageId,
                itemId: item.itemId,
                mgr: manager,
              }),
            };
            const runtimepath = runtimePath({
              appUid,
              pageId,
              itemId: activeItemId,
              key: property,
            });
            runtimepaths.push(runtimepath);
            if (!preventCollapse) {
              Object.keys(_props).forEach((key) => {
                if (
                  _props[key] === undefined ||
                  areEqualShallow(manager.defaultValue[key], _props[key])
                ) {
                  // just delete instead of setting null as we are not using
                  // JSON_MERGE_PATCH on the server side for IoPages anymore
                  delete _props[key];
                }
              });
            }
            return _props;
          },
        );
      });
      runtimepaths.forEach((p) => {
        set(p, undefined);
      });
    },
    [get, appUid, pageId, addToUndo, activeItemId, set, preventCollapse],
  );
  const keys = Object.keys(properties);

  const fields = keys.map((key) => {
    const prop = properties[key];
    // @ts-ignore dynamic key
    let value = props[key];
    if (value === undefined || value === null) {
      value = getDefaultValues(mgr, key);
    }

    return (
      <DeferredEditor
        activeItemId={activeItemId}
        def={prop}
        key={`${key}-${activeItemId}`}
        pageId={pageId}
        property={key}
        mgr={mgr}
        value={value}
        onChange={onChange}
        hmr={hmr}
        appUid={appUid}
        platform={platform}
      />
    );
  });

  return (
    <Accordion
      className={classes.root}
      expanded={preventCollapse || expanded}
      onChange={preventCollapse ? undefined : () => setExpanded((e) => !e)}
      sx={
        preventCollapse
          ? {
              '& .MuiAccordionSummary-content': {
                cursor: 'default',
              },
            }
          : undefined
      }
    >
      <AccordionSummary
        className={classes.header}
        expandIcon={<Icon iconName="chevron-down" />}
        sx={
          preventCollapse
            ? {
                '& .MuiAccordionSummary-expandIconWrapper': { display: 'none' },
              }
            : undefined
        }
      >
        <Typography className={classes.heading}>{section}</Typography>
      </AccordionSummary>
      <AccordionDetails className={classes.details}>
        <div className={classes.form}>{fields}</div>
      </AccordionDetails>
    </Accordion>
  );
}
